import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiEndpoints, ApiResponse, getApiClient } from "../../../api-http";
import { getApiLocale } from "../../../api-http/get-api-locale";
import { useLocaleContext } from "../../../i18n/locale-context";
import { JobData } from "../types";

export interface UseGetJobsOptions {
  autoFirstFetch?: boolean;
}

export function useGetJobs(options?: UseGetJobsOptions) {
  const { locale } = useLocaleContext();
  const { autoFirstFetch } = options ?? {};
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<JobData[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const apiLocale = getApiLocale(locale);
    setLoading(true);
    try {
      const response = await getApiClient().get<ApiResponse<JobData[]>>(
        ApiEndpoints.JOB_POSITIONS,
        { headers: { "Accept-Language": apiLocale } }
      );
      setData(response.data?.data ?? []);
      setError(null);
    } catch (e: any) {
      setError(e.hasOwnProperty("message") ? e.message : e.toString());
    }
    setLoading(false);
  }, [locale]);

  const hasFetchedOnce = useRef(false);

  useEffect(() => {
    if (autoFirstFetch && !hasFetchedOnce.current) {
      fetchData();
      hasFetchedOnce.current = true;
    }
  }, [fetchData, autoFirstFetch, hasFetchedOnce]);

  return useMemo(
    () => ({
      jobs: data,
      loading,
      error,
      fetchData,
    }),
    [data, loading, error, fetchData]
  );
}
